import ShortUniqueId from 'short-unique-id';
import { COLLECTION_TYPES, notificationTypes } from './Global';
import { toast } from '@repo/stores/Messaging';

// --- Class join codes --- //

const CLASS_CODE_TTL_DAYS = 7;
const CLASS_CODE_TTL_MS = CLASS_CODE_TTL_DAYS * 24 * 60 * 60 * 1000;

// --- Identity/Crypto --- //

const codeDictionary = [
    '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
    'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'
];

function UID() {
    return Number(new Date().getTime() + '' + Math.round(Math.random() * 100000)).toString(16);
}

function getUniqueCodeID() {
    const len = 9;
    const uid = new ShortUniqueId({
        dictionary: codeDictionary
    });
    return uid.randomUUID(len);
}

function getUserCodeID(collection) {
    const len = collection === COLLECTION_TYPES.CLASSES ? 11 : 8;
    const uid = new ShortUniqueId({
        dictionary: codeDictionary
    });
    let code = uid.randomUUID(len);

    switch (collection) {
        case COLLECTION_TYPES.DISTRICTS:
            code = 'D' + code;
            break;
        case COLLECTION_TYPES.SCHOOLS:
            code = 'S' + code;
            break;
        case COLLECTION_TYPES.CLASS_GROUPS:
            code = 'C' + code;
            break;
        case COLLECTION_TYPES.CLASSES:
            code = code + 'C';
            break;
        case COLLECTION_TYPES.SUBSCRIPTIONS:
            code = code + 'S';
            break;

    }

    return code;
}

function getUserCodeType(code) {
    let type = null;
    if (code.length === 9) {
        switch (code.slice(0, 1)) {
            case 'D': type = COLLECTION_TYPES.DISTRICTS; break;
            case 'S': type = COLLECTION_TYPES.SCHOOLS; break;
            case 'C': type = COLLECTION_TYPES.CLASS_GROUPS; break;
        }

        switch (code.slice(8)) {
            case 'C': type = COLLECTION_TYPES.CLASSES; break;
            case 'S': type = COLLECTION_TYPES.SUBSCRIPTIONS; break;
        }
    } else if (code.length === 12 && code.slice(-1) === 'C') {
        // Legacy class codes are 9 chars; new class codes are 12.
        type = COLLECTION_TYPES.CLASSES;
    }

    return type;
}

// --- Helpers --- //

function getYTIdFromURL(url) {
    let yid = url.match(/(v=|be\/)([a-zA-Z0-9-+_]+)(?=&|\/|\?|\b|$)/g)[0];
    yid = yid.replace('be/', '');
    yid = yid.replace('v=', '');
    return yid;
}

// --- DOM --- //

function isElement(obj) {
    try {
        // Using W3 DOM2 (works for FF, Opera and Chrome)
        return obj instanceof HTMLElement;
    }
    catch (e) {
        // Browsers not supporting W3 DOM2 don't have HTMLElement and
        // an exception is thrown and we end up here. Testing some
        // properties that all elements have (works on IE7)
        return (typeof obj === 'object') &&
        (obj.nodeType === 1) && (typeof obj.style === 'object') &&
        (typeof obj.ownerDocument === 'object');
    }
}

/**
 * Gets a list of focusable elements
 * 
 * @param {*} container only get focusable elements within this element
 * @returns NodeList of focusable elements
 */
function getFocusableElements(container) {
    const focusableElements = 'button:not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

    let modal;
    if (typeof container === 'string') {
        modal = document.querySelector(container);
    } else if (container instanceof Element) {
        modal = container;
    } else {
        modal = document;
    }
    
    return modal?.querySelectorAll(focusableElements) || [];
}

/**
 * will focus on the first focusable element
 * 
 * @param {*} container focus on the first element within this container
 * @returns 
 */
function focusOnFirst(container) {
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length > 0) {
        focusableElements[0].focus();
    }
}

/**
 * Focuses on the next visible focusable element on the page after the current container.
 * 
 * @param {*} container The current container from which to find the next focusable element.
 */
function focusNextElement(container) {
    const allFocusable = Array.from(getFocusableElements());

    // Get the last focusable element inside the current container
    const containedFocusable = Array.from(getFocusableElements(container));
    const lastContainedFocusable = containedFocusable.slice(-1)[0];

    // Find the index of the last focusable element inside the container in the list of all focusable elements
    const lastElementIndex = allFocusable.findIndex(element => element === lastContainedFocusable);

    // Find and focus the next focusable element, if it exists
    if (lastElementIndex !== -1 && lastElementIndex + 1 < allFocusable.length) {
        const nextFocusableElement = allFocusable[lastElementIndex + 1];
        nextFocusableElement.focus();
    }
}

function focusPreviousElement(container) {
    const allFocusable = Array.from(getFocusableElements());

    // Get the first focusable element inside the current container
    const containedFocusable = Array.from(getFocusableElements(container));
    const firstContainedFocusable = containedFocusable[0];

    // Find the index of the first focusable element inside the container in the list of all focusable elements
    const firstElementIndex = allFocusable.findIndex(element => element === firstContainedFocusable);

    // Find and focus the previous focusable element, if it exists
    if (firstElementIndex !== -1 && firstElementIndex - 1 >= 0) {
        const previousFocusableElement = allFocusable[firstElementIndex - 1];
        previousFocusableElement.focus();
    }
}

function localStorageAvailable(type) {
    let storage;
    try {
        storage = window[type];
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    } catch (e) {
        return e instanceof DOMException && (
        // everything except Firefox
            e.code === 22 ||
      // Firefox
      e.code === 1014 ||
      // test name field too, because code might not be present
      // everything except Firefox
      e.name === 'QuotaExceededError' ||
      // Firefox
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      (storage && storage.length !== 0);
    }
}

// --- Geometry --- //

function rectanglesIntersect(ax1, ay1, ax2, ay2, bx1, by1, bx2, by2) {
    return !(ax1 > bx2 || ax2 < bx1 || ay1 > by2 || ay2 < by1); 
}

// Convert string to slug
function slugify(text) {
    return text.toString().toLowerCase()
        .normalize('NFD')                   // Normalize to NFD Unicode form
        .replace(/[\u0300-\u036f]/g, '')    // Remove diacritics
        .replace(/\s+/g, '-')               // Replace spaces with -
        .replace(/[^\w-]+/g, '')            // Remove all non-word chars
        .replace(/--+/g, '-')               // Replace multiple - with single -
        .trim()                             // Trim whitespace at start and end
        .replace(/^-+|-+$/g, '');           // Trim - from start and end
}


/**
 * Checks if an element is visible in the viewport.
 * @param {Element} elem - The element to check visibility for.
 * @param {Object} options - The options for checking visibility.
 * @param {boolean} [options.partialVisibility=false] - Whether to consider partial visibility.
 * @param {number} [options.opacityThreshold=0.1] - The minimum opacity threshold for considering an element visible.
 * @returns {boolean} - True if the element is visible, false otherwise.
 */
function isVisible(elem, {partialVisibility = false, opacityThreshold = 0.1} = {}) {
    if (!(elem instanceof Element)) throw new Error('DomUtil: elem is not an element.');
    const style = getComputedStyle(elem);
    if (style.display === 'none' || style.visibility !== 'visible' || parseFloat(style.opacity) < opacityThreshold) {
        return false;
    }

    const rect = elem.getBoundingClientRect();
    const elemArea = rect.width * rect.height;
    if (elemArea === 0) return false; // Element has no size

    const viewport = {
        width: document.documentElement.clientWidth || window.innerWidth,
        height: document.documentElement.clientHeight || window.innerHeight
    };

    // Check for any part of the element in the viewport (for partial visibility)
    const inViewport = partialVisibility ? 
        (rect.bottom > 0 && rect.right > 0 && rect.top < viewport.height && rect.left < viewport.width) :
        (rect.top >= 0 && rect.left >= 0 && rect.bottom <= viewport.height && rect.right <= viewport.width);
    if (!inViewport) return false;

    return true; // Passes all checks
}

/**
 * Downloads a CSV file with the given filename and data.
 * 
 * @param {string|Array} csvData 
 * @param {string} filename string to use as the filename
 */
function downloadCSV(csvData, filename = 'download.csv') {
    let _csvData = null;
    if (typeof csvData === 'string') {
        _csvData = csvData;
    } else if (Array.isArray(csvData)) {
        // Convert array of objects to CSV string
        const keys = Object.keys(csvData[0]);
        const header = keys.join(',') + '\n';
        const rows = csvData.map(row => {
            return keys.map(key => {
                const value = row[key];
                return typeof value === 'string' ? `"${value}"` : value;
            }).join(',');
        }).join('\n');
        _csvData = header + rows;
    } else {
        throw new Error('csvData must be a string or an array');
    }


    // Create a Blob with the CSV data
    const blob = new Blob([_csvData], { type: 'text/csv' });

    // Create an anchor tag for our Blob
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);

    // Check if the filename has the .csv extension
    if (!filename.endsWith('.csv')) {
        filename += '.csv';
    }
    a.setAttribute('download', filename);

    // Append the anchor tag and remove it after automatic click
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

/**
 * Delays `func` until `delay` ms have elapsed without another call.
 *
 * @param {Function} func
 * @param {number} delay
 */
function debounce(func, delay) {
    let timeoutId = null;
    let pending = null;

    function debounced(...args) {
        const context = this;

        clearTimeout(timeoutId);
        pending = () => func.apply(context, args);
        timeoutId = setTimeout(() => {
            timeoutId = null;
            const run = pending;
            pending = null;
            run();
        }, delay);
    }

    debounced.flush = () => {
        if (!pending) return;

        clearTimeout(timeoutId);
        timeoutId = null;

        const run = pending;
        pending = null;
        run();
    };

    debounced.cancel = () => {
        clearTimeout(timeoutId);
        timeoutId = null;
        pending = null;
    };

    return debounced;
}

/**
 * @param {string} text
 */
async function copyToClipboard(text, message = 'Copied to clipboard.') {
    if (!navigator.clipboard) {
        console.error('Clipboard API is not available.');
        toast.set({
            type: notificationTypes.ERROR,
            message: 'Clipboard API is not supported on this browser.'
        });
        return;
    }
    try {
        await navigator.clipboard.writeText(text);
        toast.set({
            type: notificationTypes.SUCCESS, 
            message: message
        });
    } catch (err) {
        console.error('Failed to copy text: ', err);
        toast.set({
            type: notificationTypes.ERROR, 
            message: 'Failed to copy to clipboard'
        });
    }
}

/**
 * Get initials from a display name (e.g. "John Doe" → "JD").
 * @param {string} name
 * @param {string} [fallback='']
 * @returns {string}
 */
function getInitials(name, fallback = '') {
    if (!name) return fallback;
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) return fallback;
    return parts[0].substring(0, 1).toUpperCase()
        + (parts.length > 1 ? parts[parts.length - 1].substring(0, 1).toUpperCase() : '');
}

export {
    UID,
    isElement,
    rectanglesIntersect,
    getUniqueCodeID,
    getUserCodeID,
    getUserCodeType,
    localStorageAvailable,
    getYTIdFromURL,
    getFocusableElements,
    focusOnFirst,
    focusNextElement,
    focusPreviousElement,
    slugify,
    isVisible,
    downloadCSV,
    debounce,
    copyToClipboard,
    getInitials,
    CLASS_CODE_TTL_DAYS,
    CLASS_CODE_TTL_MS,
};